/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useSetRecoilState } from "recoil";
import { TagApi } from "../../../api/Tags";
import { CategoriesAtom } from "../../../atoms/CategoriesAtom";
import { TagsMoreActionsPopOverLoadingAtom } from "../../../atoms/MoreActionsPopoverAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import useClickOutside from "../../../hook/useClickOutside";
import { customTooltip, Flex, notSelect } from "../../../styles/general";
import { ITag } from "../../../types/TagTypes";
import IconBerserkerMail from "../../Icon";
import CreateOrUpdateTag from "../../Modal/container/Tags/CreateOrUpdateTag";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";

const PopoverContentCSS = css`
  width: 200px;

  background: #fff;

  border: 1px solid #cccccc40;
`;

const PopoverBodyCSS = css`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  grid-gap: 8px;
`;

const Container = styled.div`
  position: relative;
  margin-left: 5px;
  color: #2f2f2f;
  ${notSelect}
`;

const Row = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  padding: 10px 15px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;

    path {
      fill: white;
    }
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

const Text = styled.span`
  margin-left: 8px;
`;

interface Props {
  tag: ITag;
}

export default function MoreActionsPopOver(props: Props): JSX.Element {
  const { tag } = props;
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [bottomPopover, setBottomPopover] = useState(true);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const setCurrentCategories = useSetRecoilState(CategoriesAtom);
  const setTags = useSetRecoilState(TagsAtom);
  const setIsLoading = useSetRecoilState<boolean>(
    TagsMoreActionsPopOverLoadingAtom
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const modal = useModal();

  useEffect(() => {
    if (!containerRef.current) return;
    let bottom = containerRef.current.getBoundingClientRect().bottom;

    if (!bottomPopover)
      bottom =
        bottom + containerRef.current.getBoundingClientRect().height + 20;

    setBottomPopover(bottom >= 0 && bottom <= window.innerHeight);
  }, [containerRef, openDropView]);

  async function deleteTag() {
    try {
      setIsLoading(true);
      const removedTag = await TagApi.remove(tag._id!);
      if ((removedTag as any).code === "TAG_USED_BY_CAMPAIGN") {
        toast("Can not delete a tag used to trigger a campaign", {
          type: "error",
        });
      } else {
        setTags((tags) => tags.filter((t) => t._id !== tag._id));
      }
    } catch (error) {
      toast("There was an error, try again later", { type: "error" });
    } finally {
      setIsLoading(false);
      modal.popModal();
    }
  }

  return (
    <Container ref={wrapperRef}>
      <Popover placement="right-end">
        <PopoverTrigger>
          <Flex
            style={{ height: "30px", cursor: "pointer" }}
            data-tip
            data-for="moreActions"
            onClick={() => setOpenDropView(!openDropView)}
          >
            <ReactTooltip
              id="moreActions"
              place="bottom"
              backgroundColor="#333333"
              css={customTooltip}
              effect="solid"
              globalEventOff="click"
            >
              <TooltipText>More Actions</TooltipText>
            </ReactTooltip>

            <div
              css={css`
                cursor: pointer;
              `}
              onClick={() => setOpenDropView(!openDropView)}
            >
              <IconBerserkerMail name="threedots" />
            </div>
          </Flex>
        </PopoverTrigger>
        <PopoverContent css={PopoverContentCSS}>
          <PopoverBody css={PopoverBodyCSS}>
            <Title>TAG ACTIONS</Title>

            <Row
              onClick={() =>
                modal.pushModal(
                  <CreateOrUpdateTag
                    isEdit
                    tag={tag}
                    onCancel={() => modal.popModal()}
                  />,
                  "Update tag"
                )
              }
            >
              <IconBerserkerMail
                name="pen"
                color="#BBBBBB"
                height={16}
                width={14}
              />
              <Text>Edit tag</Text>
            </Row>

            <Row
              onClick={() => {
                modal.pushModal(
                  <Warning
                    icon={"./icons/delete.svg"}
                    title="Delete Tag?"
                    description={
                      tag.contactCount && tag.contactCount > 0
                        ? `Your “${tag.name}” tag has been applied to\n ${
                            tag.contactCount
                          } contact${
                            tag.contactCount > 1 ? "s" : ""
                          }. Are you sure you want to\n permanently delete it?`
                        : `This action cannot be reversed. Are you sure you want to delete “${tag.name}” permanently?`
                    }
                    buttonsName={["No", "Yes"]}
                    IsLoadingAtom={TagsMoreActionsPopOverLoadingAtom}
                    onOkClicked={() => {
                      deleteTag();
                    }}
                    onCancelClicked={() => modal.popModal()}
                  />
                );
              }}
            >
              <IconBerserkerMail name="delete" color="#BBBBBB" />
              <Text>Delete tag</Text>
            </Row>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Container>
  );
}
